'use client';

import { useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';


const LeadNotificationSystem = () => {
  const lastLeadCountRef = useRef<number>(-1);
  const isInitializedRef = useRef(false);

  const checkForNewLeads = useCallback(() => {
    try {
      const leads = JSON.parse(localStorage.getItem('bookingLeads') || '[]');
      const currentCount = leads.length;

      if (!isInitializedRef.current) {
        lastLeadCountRef.current = currentCount;
        isInitializedRef.current = true;
        return;
      }

      if (currentCount > lastLeadCountRef.current) {
        const newLeadsCount = currentCount - lastLeadCountRef.current;
        const latestLead = leads[0];
        lastLeadCountRef.current = currentCount;

        // Fire toast notification for new lead
        toast.custom(
          (t) => (
            <div
              className={`${
                t.visible ? 'animate-slide-in-right' : 'opacity-0'
              } max-w-sm w-full bg-white shadow-2xl rounded-xl pointer-events-auto flex border-l-4 border-green-500 overflow-hidden`}
            >
              <div className="flex-1 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-gray-900">
                      🔔 New Lead Received!
                    </p>
                    <p className="text-sm text-gray-700 font-semibold mt-0.5 truncate">
                      {latestLead?.name || 'Anonymous'} — {latestLead?.bookingDetails?.destination || latestLead?.bookingDetails?.type || 'Travel Enquiry'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {latestLead?.email || ''} {latestLead?.phone ? `· ${latestLead.phone}` : ''}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800">
                        {newLeadsCount} new {newLeadsCount === 1 ? 'lead' : 'leads'}
                      </span>
                      <a
                        href="/lead-dashboard"
                        className="text-xs font-bold text-blue-600 hover:text-blue-800 underline"
                        onClick={() => toast.dismiss(t.id)}
                      >
                        View Dashboard →
                      </a>
                    </div>
                  </div>
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ),
          {
            duration: 8000,
            position: 'top-right',
          }
        );

        // Also play a subtle notification sound via title flash
        const originalTitle = document.title;
        let flashCount = 0;
        const flashInterval = setInterval(() => {
          document.title = flashCount % 2 === 0 ? `🔔 New Lead! — ${originalTitle}` : originalTitle;
          flashCount++;
          if (flashCount >= 6) {
            clearInterval(flashInterval);
            document.title = originalTitle;
          }
        }, 600);
      }
    } catch (e) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    // Initialize count
    checkForNewLeads();

    // Poll every 2 seconds for new leads
    const interval = setInterval(checkForNewLeads, 2000);

    // Also listen to storage events (cross-tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'bookingLeads') {
        checkForNewLeads();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkForNewLeads]);

  return null;
};

export default LeadNotificationSystem;
